import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, type AlertApiRecord } from "../api/rest";
import { useSiteStore } from "../stores/siteStore";
import { useUIStore } from "../stores/uiStore";
import { useLiveCount } from "../hooks/useLiveCount";
import { getAssistantStatus, type ChatContext, type Provenance } from "./assistantApi";
import { useAssistantStore, type ChatMessage, type Step } from "./assistantStore";
import { pageHints, relatedAlerts } from "./pageHints";
import { MiniMarkdown } from "./MiniMarkdown";
import css from "./AssistantPanel.module.css";

/**
 * ADR 379 — the chat panel: a drawer on every page that asks the question API
 * through the planner and shows the work (which tools ran, with what) under
 * each reply. Page-aware: the suggestion chips, the related open alerts and
 * the live-count line change with the route, and the same context rides with
 * every message so the model answers about what the user is looking at.
 */
export function AssistantPanel() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { runId: routeRunId } = useParams();
  const open = useAssistantStore((s) => s.open);
  const setOpen = useAssistantStore((s) => s.setOpen);
  const messages = useAssistantStore((s) => s.messages);
  const busy = useAssistantStore((s) => s.busy);
  const send = useAssistantStore((s) => s.send);
  const clear = useAssistantStore((s) => s.clear);
  const siteId = useSiteStore((s) => s.activeSiteId);
  const warehouseId = useSiteStore((s) => s.activeWarehouseId);
  const runSelection = useUIStore((s) => s.runSelection);
  const liveCount = useLiveCount();
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const status = useQuery({
    queryKey: ["assistant-status", siteId],
    queryFn: () => getAssistantStatus(siteId),
    enabled: open && !!siteId,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const alerts = useQuery({
    queryKey: ["alerts", siteId, "open"],
    queryFn: () => api.listAlerts(siteId, "open"),
    enabled: open && !!siteId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const hints = useMemo(() => pageHints(pathname, { liveCount: !!liveCount }), [pathname, liveCount]);
  const related = useMemo(() => relatedAlerts(alerts.data ?? [], hints), [alerts.data, hints]);
  // The run in scope: the route's on /inventory/runs/:id, else the /live selection when it is one run.
  const runId = routeRunId ?? (runSelection && runSelection.runIds.length === 1 ? runSelection.runIds[0] : undefined);

  const context: ChatContext = useMemo(() => ({
    page: pathname,
    warehouseId: warehouseId ?? undefined,
    runId,
    locale: i18n.language,
    alerts: related.map((a) => ({ code: a.code, severity: a.severity, subject: a.subject, ts: a.ts, assetId: a.assetId })),
    liveCount: liveCount ? {
      visited: liveCount.visited, total: liveCount.total, pct: liveCount.pct, ratePerHour: liveCount.ratePerHour,
      etaIso: liveCount.etaMs ? new Date(liveCount.etaMs).toISOString() : null,
    } : undefined,
  }), [pathname, warehouseId, runId, i18n.language, related, liveCount]);

  useEffect(() => {
    // jsdom has no scrollTo; the scroll is a convenience, never a requirement.
    const el = listRef.current;
    if (open && el && typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight });
  }, [messages, open]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  if (!open) return null;

  const configured = status.data?.configured ?? true;   // optimistic until known: the input stays usable
  const ask = (text: string) => {
    if (!siteId || busy) return;
    setDraft("");
    void send(siteId, text, context);
  };
  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(draft); }
  };

  return (
    <aside className={css.panel} aria-label={t("assistant.title")}>
      <header className={css.head}>
        <div>
          <div className={css.title}>{t("assistant.title")}</div>
          <div className={css.scope}>{t(`assistant.scope.${hints.scope}`)}</div>
        </div>
        <div className={css.headBtns}>
          {messages.length > 0 && (
            <button type="button" className={css.ghostBtn} onClick={clear} disabled={busy}>{t("assistant.clear")}</button>
          )}
          <button type="button" className={css.ghostBtn} onClick={() => setOpen(false)} aria-label={t("assistant.close")}>×</button>
        </div>
      </header>

      {status.data && !status.data.configured && (
        <div className={css.notice} role="status">{t("assistant.notConfigured")}</div>
      )}

      <div className={css.context}>
        {related.length > 0 && (
          <ul className={css.alerts} aria-label={t("assistant.relatedAlerts")}>
            {related.map((a) => <AlertRow key={a.id} alert={a} />)}
          </ul>
        )}
        {liveCount && liveCount.pct !== null && (
          <div className={css.liveLine}>
            {t("assistant.liveCount", {
              pct: liveCount.pct.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
            })}
          </div>
        )}
      </div>

      <div className={css.list} ref={listRef}>
        {messages.length === 0 && (
          <div className={css.hintsBlock}>
            <div className={css.hintsTitle}>{t("assistant.tryAsking")}</div>
            <div className={css.chips}>
              {hints.questions.map((q) => (
                <button key={q} type="button" className={css.chip} disabled={!configured || busy}
                        onClick={() => ask(t(`assistant.q.${q}`))}>
                  {t(`assistant.q.${q}`)}
                </button>
              ))}
            </div>
            <p className={css.stance}>{t("assistant.stance")}</p>
          </div>
        )}
        {messages.map((m) => <Message key={m.id} m={m} />)}
      </div>

      <form className={css.composer} onSubmit={(e) => { e.preventDefault(); ask(draft); }}>
        <textarea
          ref={inputRef}
          className={css.input}
          rows={2}
          value={draft}
          placeholder={t("assistant.placeholder")}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          disabled={!configured}
          aria-label={t("assistant.placeholder")}
        />
        <button type="submit" className={css.sendBtn} disabled={!configured || busy || !draft.trim()}>
          {busy ? t("assistant.working") : t("assistant.send")}
        </button>
      </form>
    </aside>
  );
}

function AlertRow({ alert }: { alert: AlertApiRecord }) {
  const { t, i18n } = useTranslation();
  const when = new Date(alert.ts).toLocaleString(i18n.language, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  return (
    <li className={css.alert} data-sev={alert.severity}>
      <span className={css.sevDot} aria-hidden="true" />
      <span className={css.alertCode}>{alert.code}</span>
      <span className={css.alertMeta}>
        {t(`alerts.sev.${alert.severity}`)} · {alert.assetId ?? alert.subject ?? ""} · {when}
      </span>
    </li>
  );
}

function Message({ m }: { m: ChatMessage }) {
  const { t } = useTranslation();
  if (m.role === "user") return <div className={css.user}>{m.text}</div>;
  const pending = !m.text && !m.error;
  return (
    <div className={css.assistant}>
      {m.steps.length > 0 && (
        <ol className={css.steps} aria-label={t("assistant.steps")}>
          {m.steps.map((s, i) => <StepRow key={i} step={s} />)}
        </ol>
      )}
      {pending && m.steps.length === 0 && <div className={css.thinking}>{t("assistant.thinking")}</div>}
      {m.text && <div className={css.reply}><MiniMarkdown text={m.text} /></div>}
      {m.truncated && <div className={css.warn}>{t("assistant.truncated")}</div>}
      {m.error && <div className={css.error}>{t("assistant.error", { message: m.error })}</div>}
      {m.provenances.length > 0 && <ProvenanceFooter list={m.provenances} />}
    </div>
  );
}

function StepRow({ step }: { step: Step }) {
  const { t } = useTranslation();
  const args = Object.entries(step.args).filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${String(v)}`).join(" ");
  return (
    <li className={css.step} data-state={step.error ? "error" : step.done ? "done" : "running"}>
      <span className={css.stepName}>{t(`assistant.tool.${step.name}`, { defaultValue: step.name })}</span>
      {args && <span className={css.stepArgs}>{args}</span>}
      {step.error && <span className={css.stepErr}>{step.error}</span>}
    </li>
  );
}

function ProvenanceFooter({ list }: { list: Provenance[] }) {
  const { t, i18n } = useTranslation();
  // One line per distinct source: a run, a snapshot as-of, a feed window.
  const seen = new Set<string>();
  const lines: { key: string; text: string; link?: string }[] = [];
  for (const p of list) {
    const parts: string[] = [];
    if (p.runName) parts.push(t("assistant.prov.run", { name: p.runName, date: p.runCreatedAt ? new Date(p.runCreatedAt).toLocaleDateString(i18n.language) : "" }));
    if (p.snapshotAsOf) parts.push(t("assistant.prov.snapshot", { date: new Date(p.snapshotAsOf).toLocaleDateString(i18n.language) }));
    if (p.window) parts.push(t("assistant.prov.window", { from: p.window.from, to: p.window.to, covered: p.window.coveredDays }));
    const text = parts.join(" · ");
    if (!text || seen.has(text)) continue;
    seen.add(text);
    lines.push({ key: text, text, link: p.link ?? undefined });
  }
  if (lines.length === 0) return null;
  return (
    <div className={css.prov}>
      <span className={css.provLabel}>{t("assistant.prov.label")}</span>
      {lines.map((l) => (
        <span key={l.key} className={css.provLine}>
          {l.text}{l.link && <> · <a href={l.link} className={css.provLink}>{t("assistant.prov.open")}</a></>}
        </span>
      ))}
    </div>
  );
}
